import React, { useEffect, useState } from "react";

import { useDispatch } from "react-redux";

import { ReduxTrigger, Modal, ModalBody, ModalHeader, ModalFooter, openModal } from "Cobalt/Modal";
import Button from "Common/Button";
import Header from "Cobalt/Header";
import { Div, Ul, Li } from "Common/HTML";

const LOCAL_STORAGE_KEY = "quote_page_has_seen_census_processed_one_time_modal";

type Props = {
  parsedCensus?: {
    employee_profiles: Array<any>;
    employees_missing_ages: Array<any>;
    terminated_employees: Array<any>;
    error: string | null;
  };
};

function CensusProcessedModalContent({ autoOpen, parsedCensus, onHide, onEnterAges }) {
  return (
    <Modal show={autoOpen} onHide={onHide}>
      <ModalHeader closeButton>
        <Header level={7} mb={0}>
          Welcome to SimplyInsured!
        </Header>
      </ModalHeader>

      <ModalBody>
        <Div>We have processed your employee census file and imported your employees.</Div>

        {parsedCensus?.employees_missing_ages?.length !== 0 && (
          <Div mt={4}>
            We set the following employees to the default age of 30 as we could not find a date of birth or age in your census file:
            <Ul mt={2}>
              {parsedCensus.employees_missing_ages.map((name, i) => (
                <Li key={i}>{name}</Li>
              ))}
            </Ul>
          </Div>
        )}

        {parsedCensus?.terminated_employees?.length !== 0 && (
          <Div mt={4}>
            We omitted the following employees which had a termination date in your census file:
            <Ul mt={2}>
              {parsedCensus.terminated_employees.map((name, i) => (
                <Li key={i}>{name}</Li>
              ))}
            </Ul>
          </Div>
        )}

        <Button onClick={onEnterAges} color="link" mt={4}>
          Click here to review and edit employee ages
        </Button>
      </ModalBody>

      <ModalFooter>
        <Button onClick={onHide}>Done</Button>
      </ModalFooter>
    </Modal>
  );
}

function CensusProcessedModal({ parsedCensus }: Props) {
  const [autoOpen, setAutoOpen] = useState(false);
  const [hasCheckedStorage, setHasCheckedStorage] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    const enabled = parsedCensus !== undefined;

    if (!enabled || hasCheckedStorage) return;

    try {
      const hasSeen = window.localStorage.getItem(LOCAL_STORAGE_KEY) === "true";

      setAutoOpen(!hasSeen); // open only if not seen
      setHasCheckedStorage(true); // don't re-check every render
    } catch {
      // If localStorage blows up, best effort: show once this session
      setAutoOpen(true);
      setHasCheckedStorage(true);
    }
  }, [parsedCensus, hasCheckedStorage]);

  // If it's not enabled or we haven't finished the check yet, render nothing.
  if (!parsedCensus || !hasCheckedStorage) return null;

  return (
    <ReduxTrigger
      name="censusProcessedModal"
      /* eslint-disable-next-line react/no-unstable-nested-components */
      modal={(props) => {
        const handleHide = () => {
          props.onHide("censusProcessedModal");
          window.localStorage.setItem(LOCAL_STORAGE_KEY, "true");
          setAutoOpen(false);
        };

        const handleEnterAges = () => {
          props.onHide("censusProcessedModal");
          window.localStorage.setItem(LOCAL_STORAGE_KEY, "true");
          setAutoOpen(false);
          dispatch(openModal("enterAgesModal"));
        };

        return <CensusProcessedModalContent autoOpen={autoOpen} parsedCensus={parsedCensus} onHide={handleHide} onEnterAges={handleEnterAges} />;
      }}
    />
  );
}

export default CensusProcessedModal;
